// Secure token storage utility

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  issuedAt: number;
}

const PROACTIVE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

class SecureTokenStorage {
  private readonly ACCESS_TOKEN_KEY = 'jarvis_access_token';
  private readonly REFRESH_TOKEN_KEY = 'jarvis_refresh_token';
  private readonly TOKEN_METADATA_KEY = 'jarvis_token_meta';

  setTokens(accessToken: string, refreshToken: string, expiresIn?: number): void {
    const now = Date.now();
    const accessExpiryMs = expiresIn || 4 * 60 * 60 * 1000; // 4 hours default
    const metadata: TokenData = {
      accessToken,
      refreshToken,
      expiresAt: now + accessExpiryMs,
      issuedAt: now
    };

    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(this.TOKEN_METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      console.error('Failed to store tokens:', error);
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  getAccessToken(): string | null {
    return this.getFromStorage(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.getFromStorage(this.REFRESH_TOKEN_KEY);
  }

  isAccessTokenExpiring(): boolean {
    const metadata = this.getTokenMetadata();
    if (!metadata) return true;
    const timeUntilExpiry = metadata.expiresAt - Date.now();
    return timeUntilExpiry < PROACTIVE_REFRESH_THRESHOLD_MS;
  }

  isTokenExpired(): boolean {
    try {
      const metadata = localStorage.getItem(this.TOKEN_METADATA_KEY);
      if (!metadata) return true;
      const data: TokenData = JSON.parse(metadata);
      return Date.now() > data.expiresAt;
    } catch {
      return true;
    }
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_METADATA_KEY);
    localStorage.removeItem('jarvis_last_activity');
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  hasTokens(): boolean {
    return !!(this.getFromStorage(this.ACCESS_TOKEN_KEY) && this.getFromStorage(this.REFRESH_TOKEN_KEY));
  }

  getTimeUntilSessionExpiry(): number {
    const metadata = this.getTokenMetadata();
    if (!metadata) return 0;
    return Math.max(0, metadata.expiresAt - Date.now());
  }

  getTimeUntilTokenExpiry(): number {
    const metadata = this.getTokenMetadata();
    if (!metadata) return 0;
    return Math.max(0, metadata.expiresAt - Date.now());
  }

  updateLastActivity(): void {
    localStorage.setItem('jarvis_last_activity', Date.now().toString());
  }

  isSessionValid(): boolean {
    return this.hasTokens();
  }

  attachActivityListeners(): void {}
  detachActivityListeners(): void {}

  private getTokenMetadata(): TokenData | null {
    try {
      const metadata = localStorage.getItem(this.TOKEN_METADATA_KEY);
      return metadata ? JSON.parse(metadata) : null;
    } catch {
      return null;
    }
  }

  private getFromStorage(key: string): string | null {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  }
}

export const tokenStorage = new SecureTokenStorage();
